import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { PaymentService } from '../../../api/services/payment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-payment-process-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatIconModule
  ],
  template: `
    <h2 mat-dialog-title>Process Payment</h2>
    <mat-dialog-content>
      <form [formGroup]="paymentForm" class="flex flex-col gap-4">
        
        <mat-form-field>
          <mat-label>Order ID (Optional)</mat-label>
          <input matInput formControlName="orderId" placeholder="e.g. 1" type="number">
        </mat-form-field>

        <mat-form-field>
          <mat-label>Invoice Number</mat-label>
          <input matInput formControlName="invoiceNumber" placeholder="e.g. INV-001">
        </mat-form-field>

        <mat-form-field>
          <mat-label>Amount</mat-label>
          <span matPrefix>$&nbsp;</span>
          <input matInput type="number" formControlName="amount" min="0">
          <mat-error *ngIf="paymentForm.get('amount')?.hasError('required')">Amount is required</mat-error>
          <mat-error *ngIf="paymentForm.get('amount')?.hasError('min')">Amount must be greater than 0</mat-error>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Payment Method</mat-label>
          <mat-select formControlName="paymentMethod">
            <mat-option value="CARD">
              <mat-icon>credit_card</mat-icon> Credit Card
            </mat-option>
            <mat-option value="CASH">
              <mat-icon>payments</mat-icon> Cash
            </mat-option>
            <mat-option value="DIGITAL">
              <mat-icon>phone_android</mat-icon> Digital Wallet
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Customer Name</mat-label>
          <input matInput formControlName="customerName" placeholder="e.g. John Doe">
        </mat-form-field>

        <mat-form-field>
          <mat-label>Customer Email</mat-label>
          <input matInput formControlName="customerEmail" placeholder="e.g. john@email.com" type="email">
          <mat-error *ngIf="paymentForm.get('customerEmail')?.hasError('email')">Invalid email address</mat-error>
        </mat-form-field>
        
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-raised-button color="primary" [disabled]="paymentForm.invalid || isProcessing" (click)="onSubmit()">
        <mat-icon *ngIf="isProcessing" class="animate-spin mr-2">refresh</mat-icon>
        {{ isProcessing ? 'Processing...' : 'Process Payment' }}
      </button>
    </mat-dialog-actions>
  `
})
export class PaymentProcessDialogComponent {
  paymentForm: FormGroup;
  isProcessing = false;

  constructor(
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private dialogRef: MatDialogRef<PaymentProcessDialogComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.paymentForm = this.fb.group({
      orderId: [data?.orderId || 1], // Defaulting to 1 as per example, or empty if preferred
      invoiceNumber: [data?.invoiceNumber || 'INV-001', Validators.required],
      amount: [data?.amount || '', [Validators.required, Validators.min(0.01)]],
      paymentMethod: [data?.paymentMethod || 'CARD', Validators.required],
      customerName: [data?.customerName || ''],
      customerEmail: [data?.customerEmail || '', [Validators.email]]
    });
  }

  onSubmit(): void {
    if (this.paymentForm.valid) {
      this.isProcessing = true;
      const paymentData = this.paymentForm.value;
      
      this.paymentService.processPayment(paymentData).subscribe({
        next: (result) => {
          this.snackBar.open('Payment processed successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(result);
        },
        error: (error) => {
          if (!environment.production) {
            console.error('Payment error:', error);
          }
          this.snackBar.open('Payment failed. Please try again.', 'Close', { duration: 3000, panelClass: ['error-snackbar'] });
          this.isProcessing = false;
        }
      });
    }
  }
}
